'use client'

import { memo, useState, type ReactNode } from 'react'
import Link from 'next/link'
import { IconArrowRight, IconList, IconMap, IconMapPin, IconWorld } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { ConsoleToggle } from './console-toggle'
import { GpuCharacter, HUES, type Persona } from './gpu-characters'
import type { SelectUnit } from './gpu-select'

/** One offered region, priced from the regional multiplier table. */
export interface SelectRegion {
  id: string
  name: string
  city: string
  group: string
  /** Position on the world map in percent, or null to list it without a marker. */
  x: number | null
  y: number | null
  multiplier: number
  adjustmentLabel: string
  /** Cheapest GPU rate here, formatted. */
  fromLabel: string
  /** The GPUs offered here, with their on-demand rate in this region. */
  gpus: { id: string; rateLabel: string; deployHref: string }[]
  deployHref: string
  browseHref: string
}

/**
 * Land as longitude spans per 5° latitude band, 80°N to 60°S. Drawn as a
 * dot matrix, so the coastline only needs to be right to the nearest cell.
 */
const LAND: [number, number][][] = [
  [[-120, -75], [-70, -20], [15, 25], [95, 105]],
  [[-165, -145], [-125, -75], [-60, -20], [55, 60], [70, 140]],
  [[-165, -60], [-55, -25], [-24, -14], [13, 180]],
  [[-165, -65], [-50, -40], [-24, -14], [5, 180]],
  [[-165, -155], [-135, -95], [-78, -60], [-7, -2], [5, 163]],
  [[-130, -58], [-10, 2], [3, 143]],
  [[-125, -53], [-5, 135], [140, 145]],
  [[-125, -70], [-9, 130], [139, 142]],
  [[-123, -76], [-9, 128], [132, 141]],
  [[-120, -80], [-10, 122], [130, 140]],
  [[-115, -97], [-83, -80], [-15, 122]],
  [[-110, -97], [-85, -75], [-17, 37], [39, 59], [68, 87], [92, 122]],
  [[-105, -88], [-17, 40], [42, 55], [73, 84], [94, 110], [120, 125]],
  [[-92, -83], [-75, -62], [-17, 51], [75, 80], [98, 109], [120, 126]],
  [[-78, -60], [-13, 48], [80, 82], [98, 105], [122, 126]],
  [[-80, -50], [8, 42], [95, 104], [109, 118], [120, 125]],
  [[-80, -38], [9, 41], [100, 106], [110, 117], [119, 123], [131, 150]],
  [[-80, -35], [12, 40], [105, 115], [138, 148]],
  [[-77, -38], [13, 41], [44, 50], [130, 137], [141, 144]],
  [[-72, -39], [12, 38], [43, 50], [122, 146]],
  [[-70, -41], [14, 36], [43, 48], [114, 150]],
  [[-71, -48], [15, 33], [114, 153]],
  [[-72, -52], [18, 29], [115, 152]],
  [[-73, -57], [140, 150], [174, 178]],
  [[-74, -63], [145, 148], [170, 175]],
  [[-75, -66], [167, 170]],
  [[-74, -68]],
  [],
]

const MAP_COLS = 72
const MAP_ROWS = LAND.length

const LAND_CELLS = LAND.flatMap((spans, row) =>
  Array.from({ length: MAP_COLS }, (_, col) => col).filter((col) => {
    const west = -180 + col * 5
    return spans.some(([from, to]) => west < to && west + 5 > from)
  }).map((col) => ({ row, col })),
)

/** Static, so it never re-renders when a region is picked. */
const LandDots = memo(function LandDots() {
  return (
    <svg viewBox={`0 0 ${MAP_COLS} ${MAP_ROWS}`} className="absolute inset-0 h-full w-full" aria-hidden preserveAspectRatio="none">
      {LAND_CELLS.map(({ row, col }) => (
        <rect
          key={`${row}-${col}`}
          x={col + 0.16}
          y={row + 0.16}
          width={0.68}
          height={0.68}
          rx={0.16}
          className="fill-foreground"
          opacity={0.16}
        />
      ))}
    </svg>
  )
})

/** Where each group's label sits on the map, in percent. */
const GROUP_LABELS: Record<string, { x: number; y: number }> = {
  'North America': { x: 18, y: 10 },
  Europe: { x: 50, y: 6 },
  'Asia Pacific': { x: 80, y: 66 },
  Africa: { x: 53, y: 56 },
}

export function RegionSelect({
  regions,
  groups,
  units,
  personas,
  gpuFilter,
  onGpuFilter,
}: {
  regions: SelectRegion[]
  groups: string[]
  units: SelectUnit[]
  personas: Persona[]
  gpuFilter: string | null
  onGpuFilter: (id: string | null) => void
}) {
  const [view, setView] = useState<'map' | 'list'>('map')
  const [group, setGroup] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState(regions[0]?.id ?? '')
  const [lastFilter, setLastFilter] = useState(gpuFilter)

  const offers = (region: SelectRegion, gpuId: string | null) => !gpuId || region.gpus.some((gpu) => gpu.id === gpuId)
  const visible = (region: SelectRegion) => (!group || region.group === group) && offers(region, gpuFilter)

  // When the GPU filter changes (here or from "See on map" above) and the
  // region on screen does not offer that GPU, jump to the first one that does.
  if (gpuFilter !== lastFilter) {
    setLastFilter(gpuFilter)
    const current = regions.find((region) => region.id === selectedId)
    if (current && !offers(current, gpuFilter)) {
      const first = regions.find((region) => offers(region, gpuFilter))
      if (first) setSelectedId(first.id)
    }
  }

  const region = regions.find((candidate) => candidate.id === selectedId) ?? regions[0]
  if (!region) return null

  const filterUnit = gpuFilter ? units.find((unit) => unit.id === gpuFilter) ?? null : null
  const matching = regions.filter(visible)

  const pickGroup = (next: string | null) => {
    setGroup(next)
    if (next && region.group !== next) {
      const first = regions.find((candidate) => candidate.group === next && offers(candidate, gpuFilter))
      if (first) setSelectedId(first.id)
    }
  }

  return (
    <div id="regions" className="mt-20 scroll-mt-24">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-primary">
            <IconWorld className="h-4 w-4" />
            World select · {regions.length} regions
          </span>
          <h3 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Choose where your machine runs.
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground/80 md:text-base">
            Pick the region closest to you for the lowest latency. Each region adjusts the base rate, and not every GPU
            is offered everywhere.
          </p>
        </div>
        <ConsoleToggle
          label="View"
          value={view}
          onChange={setView}
          options={[
            { value: 'map', label: 'Map', icon: IconMap },
            { value: 'list', label: 'List', icon: IconList },
          ]}
        />
      </div>

      {/* filters */}
      <div className="mt-6 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div role="group" aria-label="Filter by area" className="scrollbar-none flex gap-2 overflow-x-auto pb-1">
          <FilterChip active={group === null} onClick={() => pickGroup(null)}>
            All areas
          </FilterChip>
          {groups.map((name) => (
            <FilterChip key={name} active={group === name} onClick={() => pickGroup(group === name ? null : name)}>
              {name}
              <span className="font-mono text-xs opacity-70">{regions.filter((candidate) => candidate.group === name).length}</span>
            </FilterChip>
          ))}
        </div>
        <div role="group" aria-label="Filter by GPU" className="scrollbar-none flex gap-2 overflow-x-auto pb-1">
          <FilterChip active={gpuFilter === null} onClick={() => onGpuFilter(null)}>
            Any GPU
          </FilterChip>
          {units.map((unit, index) => (
            <FilterChip
              key={unit.id}
              active={gpuFilter === unit.id}
              onClick={() => onGpuFilter(gpuFilter === unit.id ? null : unit.id)}
            >
              <span aria-hidden className={cn('h-2 w-2 rounded-xs', HUES[personas[index].hue].bg)} />
              {unit.name}
            </FilterChip>
          ))}
        </div>
      </div>

      <p className="mt-3 font-mono text-xs text-muted-foreground" aria-live="polite">
        {matching.length} {matching.length === 1 ? 'region matches' : 'regions match'}
        {filterUnit ? ` · offering the ${filterUnit.name}` : ''}
        {group ? ` · in ${group}` : ''}
      </p>

      <div className="mt-4 grid gap-5 lg:grid-cols-12">
        <div className="lg:col-span-8">
          {view === 'map' ? (
            <WorldMap
              regions={regions}
              selectedId={region.id}
              visible={visible}
              group={group}
              groups={groups}
              onSelect={setSelectedId}
              onGroup={(name) => pickGroup(group === name ? null : name)}
            />
          ) : (
            <RegionList
              regions={matching}
              groups={groups}
              units={units}
              personas={personas}
              selectedId={region.id}
              gpuFilter={gpuFilter}
              onSelect={setSelectedId}
              onReset={() => {
                setGroup(null)
                onGpuFilter(null)
              }}
            />
          )}
        </div>

        <RegionDetail
          region={region}
          regions={regions}
          groups={groups}
          units={units}
          personas={personas}
          gpuFilter={gpuFilter}
        />
      </div>
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-medium ring-1 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'bg-foreground text-background ring-foreground'
          : 'bg-secondary/60 text-foreground/80 ring-foreground/10 hover:text-foreground hover:ring-foreground/30',
      )}
    >
      {children}
    </button>
  )
}

/* ------------------------------------------------------------------ */
/* Map                                                                 */
/* ------------------------------------------------------------------ */

function WorldMap({
  regions,
  selectedId,
  visible,
  group,
  groups,
  onSelect,
  onGroup,
}: {
  regions: SelectRegion[]
  selectedId: string
  visible: (region: SelectRegion) => boolean
  group: string | null
  groups: string[]
  onSelect: (id: string) => void
  onGroup: (name: string) => void
}) {
  return (
    <div className="relative overflow-hidden rounded-xl bg-background/60 shadow-2xl shadow-background ring-1 ring-foreground/10">
      <div aria-hidden className="select-wash absolute inset-0 opacity-60 [--hue:var(--accent)]" />
      <div className="scrollbar-none relative overflow-x-auto">
        <div className="relative aspect-[18/7] min-w-[760px]">
          <div aria-hidden className="constellation-grid absolute inset-0 opacity-25" />
          <LandDots />
          <div aria-hidden className="scanlines absolute inset-0 opacity-50" />
          <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="scan-sweep h-1/4 w-full bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
          </div>

          {/* area labels double as area filters */}
          {groups.map((name) => {
            const spot = GROUP_LABELS[name]
            if (!spot) return null
            return (
              <button
                key={name}
                type="button"
                onClick={() => onGroup(name)}
                aria-pressed={group === name}
                className={cn(
                  'absolute -translate-x-1/2 rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.24em] transition-colors',
                  group === name ? 'bg-primary/15 text-primary ring-1 ring-primary/50' : 'text-muted-foreground hover:text-foreground',
                )}
                style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
              >
                {name}
              </button>
            )
          })}

          {regions.map((region) => {
            if (region.x === null || region.y === null) return null
            const selected = region.id === selectedId
            const shown = visible(region)
            return (
              <button
                key={region.id}
                type="button"
                onClick={() => onSelect(region.id)}
                aria-pressed={selected}
                aria-label={`${region.name}: ${region.adjustmentLabel.toLowerCase()}, ${region.gpus.length} GPUs`}
                className={cn(
                  'group absolute -translate-x-1/2 -translate-y-1/2 p-2 focus-visible:outline-none',
                  selected ? 'z-30' : 'z-20',
                )}
                style={{ left: `${region.x}%`, top: `${region.y}%` }}
              >
                {selected && (
                  <>
                    <span aria-hidden className="absolute inset-0 animate-ping rounded-full bg-primary/40 motion-reduce:hidden" />
                    <span aria-hidden className="absolute bottom-1/2 left-1/2 h-12 w-0.5 -translate-x-1/2 bg-gradient-to-t from-primary to-transparent" />
                  </>
                )}
                <span
                  aria-hidden
                  className={cn(
                    'relative block rotate-45 rounded-[3px] ring-1 transition-all duration-200 group-focus-visible:ring-2 group-focus-visible:ring-ring',
                    selected
                      ? 'h-4 w-4 bg-gradient-to-br from-foreground via-primary to-primary/70 shadow-[0_0_20px] shadow-primary ring-foreground'
                      : shown
                        ? 'h-3 w-3 bg-gradient-to-br from-foreground via-accent to-accent/60 shadow-[0_0_12px] shadow-accent/70 ring-foreground/40 group-hover:scale-125'
                        : 'h-2.5 w-2.5 bg-foreground/20 ring-foreground/10 group-hover:bg-foreground/40',
                  )}
                />
                <span
                  className={cn(
                    'pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 backdrop-blur transition-all duration-200',
                    selected
                      ? 'bottom-[calc(100%+2.25rem)] bg-primary text-primary-foreground ring-primary'
                      : 'bottom-full bg-background/90 text-foreground opacity-0 ring-foreground/15 group-hover:opacity-100 group-focus-visible:opacity-100',
                  )}
                >
                  {region.city}
                  {selected && <span className="ml-1.5 font-mono">{region.adjustmentLabel}</span>}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-foreground/10 px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
        <span className="flex items-center gap-2">
          <span aria-hidden className="h-2.5 w-2.5 rotate-45 rounded-[2px] bg-primary" />
          Selected
        </span>
        <span className="flex items-center gap-2">
          <span aria-hidden className="h-2.5 w-2.5 rotate-45 rounded-[2px] bg-accent" />
          Matches filters
        </span>
        <span className="flex items-center gap-2">
          <span aria-hidden className="h-2 w-2 rotate-45 rounded-[2px] bg-foreground/25" />
          Filtered out
        </span>
        <span className="ml-auto hidden sm:inline">Click a marker or an area name</span>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* List                                                                */
/* ------------------------------------------------------------------ */

function RegionList({
  regions,
  groups,
  units,
  personas,
  selectedId,
  gpuFilter,
  onSelect,
  onReset,
}: {
  regions: SelectRegion[]
  groups: string[]
  units: SelectUnit[]
  personas: Persona[]
  selectedId: string
  gpuFilter: string | null
  onSelect: (id: string) => void
  onReset: () => void
}) {
  const shownGroups = groups
    .map((name) => ({ name, rows: regions.filter((region) => region.group === name) }))
    .filter((entry) => entry.rows.length > 0)

  if (shownGroups.length === 0) {
    return (
      <div className="flex h-full min-h-60 flex-col items-center justify-center gap-3 rounded-xl bg-background/50 p-8 text-center ring-1 ring-foreground/10">
        <p className="text-sm text-foreground/80">No region matches these filters.</p>
        <button
          type="button"
          onClick={onReset}
          className="rounded-full px-4 py-2 text-sm font-semibold text-foreground ring-1 ring-foreground/20 hover:bg-secondary"
        >
          Show all regions
        </button>
      </div>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {shownGroups.map(({ name, rows }) => (
        <div key={name} className="rounded-xl bg-background/50 p-4 ring-1 ring-foreground/10">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-heading text-base font-semibold text-foreground">{name}</span>
            <span className="font-mono text-xs text-muted-foreground">
              {rows.length} {rows.length === 1 ? 'region' : 'regions'}
            </span>
          </div>
          <ul className="mt-3 space-y-1.5">
            {rows.map((region) => {
              const selected = region.id === selectedId
              const filtered = gpuFilter ? region.gpus.find((gpu) => gpu.id === gpuFilter) : null
              return (
                <li key={region.id}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onSelect(region.id)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left ring-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      selected ? 'bg-primary/10 ring-primary/60' : 'ring-transparent hover:bg-secondary',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">{region.city}</span>
                      <span className="block text-xs text-muted-foreground">
                        {region.adjustmentLabel} · {filtered ? filtered.rateLabel : `from ${region.fromLabel}`}/hr
                      </span>
                    </span>
                    <span className="flex gap-1" aria-label={`${region.gpus.length} of ${units.length} GPUs offered`} role="img">
                      {units.map((unit, index) => (
                        <span
                          key={unit.id}
                          className={cn(
                            'h-2.5 w-2.5 rounded-xs',
                            region.gpus.some((gpu) => gpu.id === unit.id) ? HUES[personas[index].hue].bg : 'bg-foreground/10',
                          )}
                        />
                      ))}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Detail                                                              */
/* ------------------------------------------------------------------ */

function RegionDetail({
  region,
  regions,
  groups,
  units,
  personas,
  gpuFilter,
}: {
  region: SelectRegion
  regions: SelectRegion[]
  groups: string[]
  units: SelectUnit[]
  personas: Persona[]
  gpuFilter: string | null
}) {
  const world = groups.indexOf(region.group) + 1
  const stage = regions.filter((candidate) => candidate.group === region.group).findIndex((candidate) => candidate.id === region.id) + 1

  return (
    <aside
      aria-label="Selected region"
      className="relative overflow-hidden rounded-xl bg-card/80 p-5 ring-1 ring-foreground/10 lg:col-span-4"
    >
      <div aria-hidden className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-accent to-transparent" />
      <div key={region.id} className="animate-in fade-in slide-in-from-right-2 duration-300 motion-reduce:animate-none">
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{region.group}</span>
          {world > 0 && stage > 0 && (
            <span className="rounded-sm bg-secondary px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-foreground/80">
              World {world}-{stage}
            </span>
          )}
        </div>
        <h4 className="mt-1 flex items-center gap-2 font-heading text-3xl font-semibold leading-tight text-foreground">
          <IconMapPin className="h-6 w-6 shrink-0 text-primary" />
          {region.city}
        </h4>
        <p className="text-sm text-muted-foreground">{region.name}</p>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-background/60 px-3 py-2.5 ring-1 ring-foreground/10">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Price adjustment</div>
            <div className={cn('mt-0.5 font-heading text-xl font-semibold tabular-nums', region.multiplier === 1 ? 'text-primary' : 'text-foreground')}>
              {region.adjustmentLabel}
            </div>
          </div>
          <div className="rounded-lg bg-background/60 px-3 py-2.5 ring-1 ring-foreground/10">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">GPUs here</div>
            <div className="mt-0.5 font-heading text-xl font-semibold tabular-nums text-foreground">
              {region.gpus.length}
              <span className="text-sm text-muted-foreground"> / {units.length}</span>
            </div>
          </div>
        </div>

        <ul className="mt-4 divide-y divide-foreground/10">
          {units.map((unit, index) => {
            const offer = region.gpus.find((gpu) => gpu.id === unit.id)
            const persona = personas[index]
            return (
              <li
                key={unit.id}
                className={cn(
                  'flex items-center gap-3 py-2.5',
                  gpuFilter === unit.id && '-mx-2 rounded-md bg-primary/10 px-2',
                )}
              >
                <span className={cn('w-7 shrink-0', !offer && 'opacity-30')}>
                  <GpuCharacter persona={persona} uid={`region-${unit.id}`} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{unit.name}</span>
                  <span className={cn('block font-mono text-[10px] uppercase tracking-[0.18em]', HUES[persona.hue].text)}>
                    {persona.codename}
                  </span>
                </span>
                {offer ? (
                  <>
                    <span className="font-mono text-sm tabular-nums text-foreground">{offer.rateLabel}/hr</span>
                    <Link
                      href={offer.deployHref}
                      aria-label={`Deploy the ${unit.name} in ${region.city}`}
                      className="grid h-8 w-8 place-items-center rounded-full bg-secondary text-foreground ring-1 ring-foreground/15 transition-colors hover:bg-primary hover:text-primary-foreground"
                    >
                      <IconArrowRight className="h-4 w-4" />
                    </Link>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">Not offered here</span>
                )}
              </li>
            )
          })}
        </ul>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          On-demand rates in this region, before the upload-speed add-on. The create form shows the exact rate before you
          start.
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          <Link
            href={region.deployHref}
            className="arcade-press group inline-flex h-11 items-center gap-2 rounded-full bg-primary px-5 font-heading text-sm font-bold text-primary-foreground"
          >
            Deploy in {region.city}
            <IconArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link
            href={region.browseHref}
            className="inline-flex h-11 items-center rounded-full px-4 text-sm font-semibold text-foreground ring-1 ring-accent/50 transition-colors hover:bg-accent/10"
          >
            Browse GPUs here
          </Link>
        </div>
      </div>
    </aside>
  )
}
